/**
 * Session metadata + config resolution.
 *
 * Two files, secret split from identity:
 *   - `<project>/.heysegment/session.json` — identity + API base URL (non-secret).
 *   - `~/.heysegment/credentials.json`     — session tokens, keyed by base URL +
 *     session id, so a copied project dir never carries the token.
 *
 * Both are Schema-validated on read.
 *
 * @module
 */
import { Effect, FileSystem, Path, Schema as S } from "effect"
import { UsageError } from "../errors"

const DEFAULT_API_BASE_URL = "https://heysegment.com"

/** Strip trailing slashes so `${baseUrl}/rpc` never doubles up. */
export const normalizeBaseUrl = (url: string): string => url.replace(/\/+$/, "")

/** flag → env (`HEYSEGMENT_API_URL`) → saved session → default. */
export const resolveBaseUrl = (
  flag: string | undefined,
  existing: SessionFile | null,
): string =>
  normalizeBaseUrl(
    flag ??
      process.env.HEYSEGMENT_API_URL ??
      existing?.apiBaseUrl ??
      DEFAULT_API_BASE_URL,
  )

// ── session.json (project-local, non-secret) ─────────────────────────────────

export const SessionFileSchema = S.Struct({
  schemaVersion: S.Literal(1),
  sessionId: S.String,
  workspaceId: S.String,
  versionId: S.String,
  apiBaseUrl: S.String,
  createdAt: S.String,
})
export type SessionFile = typeof SessionFileSchema.Type
const decodeSession = S.decodeUnknownEffect(SessionFileSchema)

const heysegmentDir = (path: Path.Path, projectDir: string): string =>
  path.join(projectDir, ".heysegment")
const sessionFilePath = (path: Path.Path, projectDir: string): string =>
  path.join(heysegmentDir(path, projectDir), "session.json")

export const readSessionFile = (
  projectDir: string,
): Effect.Effect<SessionFile | null, UsageError, FileSystem.FileSystem | Path.Path> =>
  Effect.gen(function* () {
    const fs = yield* FileSystem.FileSystem
    const path = yield* Path.Path
    const file = sessionFilePath(path, projectDir)
    const exists = yield* fs.exists(file).pipe(Effect.orElseSucceed(() => false))
    if (!exists) return null
    const raw = yield* fs
      .readFileString(file)
      .pipe(Effect.mapError((e) => new UsageError({ reason: `Could not read ${file}: ${e.message}` })))
    const parsed = yield* Effect.try({
      try: () => JSON.parse(raw),
      catch: () => new UsageError({ reason: `Invalid JSON in ${file}` }),
    })
    return yield* decodeSession(parsed).pipe(
      Effect.mapError(() => new UsageError({ reason: `Invalid session file: ${file}` })),
    )
  })

export const writeSessionFile = (
  projectDir: string,
  session: SessionFile,
): Effect.Effect<string, UsageError, FileSystem.FileSystem | Path.Path> =>
  Effect.gen(function* () {
    const fs = yield* FileSystem.FileSystem
    const path = yield* Path.Path
    const dir = heysegmentDir(path, projectDir)
    yield* fs
      .makeDirectory(dir, { recursive: true })
      .pipe(Effect.mapError((e) => new UsageError({ reason: `Could not create ${dir}: ${e.message}` })))
    const file = sessionFilePath(path, projectDir)
    yield* fs
      .writeFileString(file, `${JSON.stringify(session, null, 2)}\n`)
      .pipe(Effect.mapError((e) => new UsageError({ reason: `Could not write ${file}: ${e.message}` })))
    return file
  })

// ── credentials.json (user-level, secret) ────────────────────────────────────

const CredentialEntrySchema = S.Struct({ token: S.String, savedAt: S.String })
const CredentialsFileSchema = S.Struct({
  version: S.Literal(1),
  credentials: S.Record(S.String, S.Record(S.String, CredentialEntrySchema)),
})
type CredentialsFile = typeof CredentialsFileSchema.Type
const decodeCredentials = S.decodeUnknownEffect(CredentialsFileSchema)

const EMPTY_CREDENTIALS: CredentialsFile = { version: 1, credentials: {} }

const homeDir = (): string | null =>
  process.env.HOME ?? process.env.USERPROFILE ?? null

export const writeCredential = (input: {
  readonly baseUrl: string
  readonly sessionId: string
  readonly token: string
  readonly savedAt: string
}): Effect.Effect<string, UsageError, FileSystem.FileSystem | Path.Path> =>
  Effect.gen(function* () {
    const fs = yield* FileSystem.FileSystem
    const path = yield* Path.Path
    const home = homeDir()
    if (home === null) {
      return yield* Effect.fail(
        new UsageError({ reason: "Cannot resolve home directory ($HOME)" }),
      )
    }
    const dir = path.join(home, ".heysegment")
    const file = path.join(dir, "credentials.json")

    yield* fs
      .makeDirectory(dir, { recursive: true })
      .pipe(Effect.mapError((e) => new UsageError({ reason: `Could not create ${dir}: ${e.message}` })))

    const existing = yield* readCredentialsFile(fs, file)
    const byBase = existing.credentials[input.baseUrl] ?? {}
    const updated: CredentialsFile = {
      version: 1,
      credentials: {
        ...existing.credentials,
        [input.baseUrl]: {
          ...byBase,
          [input.sessionId]: { token: input.token, savedAt: input.savedAt },
        },
      },
    }
    yield* fs
      .writeFileString(file, `${JSON.stringify(updated, null, 2)}\n`)
      .pipe(Effect.mapError((e) => new UsageError({ reason: `Could not write ${file}: ${e.message}` })))
    return file
  })

/** Read the saved session token for a base URL + session id; null if absent. */
export const readCredential = (
  baseUrl: string,
  sessionId: string,
): Effect.Effect<string | null, never, FileSystem.FileSystem | Path.Path> =>
  Effect.gen(function* () {
    const fs = yield* FileSystem.FileSystem
    const path = yield* Path.Path
    const home = homeDir()
    if (home === null) return null
    const file = path.join(home, ".heysegment", "credentials.json")
    const creds = yield* readCredentialsFile(fs, file)
    return creds.credentials[baseUrl]?.[sessionId]?.token ?? null
  })

const readCredentialsFile = (
  fs: FileSystem.FileSystem,
  file: string,
): Effect.Effect<CredentialsFile, never> =>
  Effect.gen(function* () {
    const exists = yield* fs.exists(file).pipe(Effect.orElseSucceed(() => false))
    if (!exists) return EMPTY_CREDENTIALS
    const raw = yield* fs.readFileString(file).pipe(Effect.orElseSucceed(() => ""))
    if (raw.trim().length === 0) return EMPTY_CREDENTIALS
    return yield* Effect.try({ try: () => JSON.parse(raw), catch: (e) => e }).pipe(
      Effect.flatMap(decodeCredentials),
      Effect.orElseSucceed(() => EMPTY_CREDENTIALS),
    )
  })

// ── .gitignore ───────────────────────────────────────────────────────────────

/** Best-effort: ensure `.heysegment/` is gitignored in the project. */
export const ensureGitignored = (
  projectDir: string,
): Effect.Effect<void, never, FileSystem.FileSystem | Path.Path> =>
  Effect.gen(function* () {
    const fs = yield* FileSystem.FileSystem
    const path = yield* Path.Path
    const file = path.join(projectDir, ".gitignore")
    const current = yield* fs.readFileString(file).pipe(Effect.orElseSucceed(() => ""))
    const already = current
      .split("\n")
      .some((line) => line.trim() === ".heysegment" || line.trim() === ".heysegment/")
    if (already) return
    const prefix = current.length > 0 && !current.endsWith("\n") ? `${current}\n` : current
    yield* fs
      .writeFileString(file, `${prefix}.heysegment/\n`)
      .pipe(Effect.orElseSucceed(() => undefined))
  })
